/**
 * Adzuki. My Settings - search results dropdown
 *
 * SET-72. SET-9 rendered the input and an empty results container; SET-71 built the index behind
 * it. This file is what puts one in the other.
 *
 * TYPE-AHEAD, NOT A FILTER (ruling, 8 Sep 2026). The left nav is never filtered as the advisor
 * types - matches appear in the dropdown and the section list stays where it was, so the thing
 * they were looking at does not move while they look for something else.
 *
 * THE INDEX IS FETCHED ONCE PER MODAL SESSION, from its own endpoint. MySettingsNavElement renders
 * in the fragment layout as well as the full one, so anything put in it is re-sent on every panel
 * navigation - and the index is around 235 rows. See MySettingsController::searchIndex().
 *
 * SELECTING A RESULT IS A GUARDED EXIT, and this file does not implement the guard. It navigates
 * through Adzuki.MySettings.requestNavigate(), the same door the left nav and the tab strip use,
 * so SET-76's discard dialog intercepts a jump away from an edited panel exactly as it intercepts
 * a subsection switch. Without that, search would be the one way out of a dirty form that never
 * asked - which is the fifth of the five guarded exits, and the only one no ticket named.
 *
 * THE SCROLL AND HIGHLIGHT CANNOT RUN AT SELECTION TIME. The target panel has not been fetched
 * yet, and when the guard takes over it may not be fetched for a while - or never, if the advisor
 * chooses Keep editing. So the target is held and consumed on the next 'my-settings:rendered',
 * and dropped if the anchor is not on the panel that arrives.
 */

(function MySettingsSearch($, Adzuki)
{
	"use strict";

	var MAX_RESULTS  = 10,
	    HIGHLIGHT_MS = 2200,
	    HIT_CLASS    = 'is-search-hit';

	// The dropdown's category column. SET-71 emits three kinds rather than the four the ticket
	// named: the nav's anchors are a flat key => label map and do not record whether an entry is a
	// block title or a single field, so 'group' and 'setting' cannot be told apart and both arrive
	// as 'setting'. "Setting" is the word an advisor reads either way.
	var CATEGORY = {
		section: 'Section',
		tab    : 'Tab',
		setting: 'Setting'
	};

	// The order results appear in, broadest first: a subsection, then a tab, then a block inside
	// it. See the sort in _match().
	var KIND_RANK = {
		section: 0,
		tab    : 1,
		setting: 2
	};

	var index   = null,
	    loading = false,
	    rows    = [],
	    active  = -1,
	    pending = null,
	    // SET-72 QA: what was typed when a jump was chosen, so Keep editing can give it back
	    last_term = '';

	var _resolve, _load, _match, _render, _close, _move, _choose, _consume, _esc, _aliasHit, _norm,
	    _input, _results, _search;

	_input = function input() { return $('#my-settings-search-input'); };
	_results = function results() { return $('#my-settings-search-results'); };

	/**
	 * The same rule my_settings.js uses: the app is not served from the domain root, and a bare
	 * /MySettings/... path needs the base putting on it exactly once.
	 */
	_resolve = function resolve(url)
	{
		var base = (Adzuki.base_uri || '');

		if (base === '' || url.indexOf('http') === 0 || url.indexOf(base + '/') === 0) {
			return url;
		}

		return base + url;
	};

	/**
	 * SET-71 QA: PUNCTUATION IS NOT A WORD, AND AN ADVISOR DOES NOT TYPE IT.
	 *
	 * "W9" found nothing because the anchor reads "W-9s on File". That is not one label's problem:
	 * thirteen labels on this surface carry |, & or /, so "cpcentral" missed "CP|central",
	 * "tax account information" missed "Tax / Account Information" and "profile preferences"
	 * missed "Profile & Preferences". Every one of them is what somebody would actually type.
	 *
	 * PUNCTUATION IS REMOVED RATHER THAN TURNED INTO A SPACE, which is the whole trick. Replacing
	 * it would make "W-9s" into "w 9s" and "W9" would still miss; removing it gives "w9s", while
	 * "Tax / Account Information" still collapses to "tax account information" because the spaces
	 * either side of the slash survive and the run is squeezed afterwards.
	 */
	_norm = function norm(text)
	{
		return (text || '')
			.toLowerCase()
			.replace(/[^a-z0-9\s]+/g, '')
			.replace(/\s+/g, ' ')
			.replace(/^ | $/g, '');
	};

	_aliasHit = function aliasHit(row, needle)
	{
		var hit = false;

		if (!row.aliases || !row.aliases.length) {
			return false;
		}

		$.each(row.aliases, function eachAlias(i, alias) {
			if (_norm(alias).indexOf(needle) > -1) {
				hit = true;

				return false;
			}
		});

		return hit;
	};

	_esc = function esc(text)
	{
		return $('<div>').text(text === null || text === undefined ? '' : text).html();
	};

	_load = function load(done)
	{
		if (index !== null) {
			done();

			return;
		}

		if (loading) {
			return;
		}

		loading = true;

		$.ajax({
			url     : _resolve('/MySettings/searchIndex'),
			dataType: 'json',
			success : function loaded(response) {
				index   = (response && response.rows ? response.rows : []);
				loading = false;
				done();
			},
			error: function failed() {
				// An empty index is the safe failure: the field keeps working as a text input and
				// the advisor falls back to the nav, rather than being shown a broken dropdown.
				index   = [];
				loading = false;
			}
		});
	};

	/**
	 * Ranked so an exact prefix beats a contained word, and a label beats a breadcrumb - typing
	 * "ship" should offer Shipping Accounts before every setting that merely lives under it.
	 */
	_match = function match(term)
	{
		var needle = _norm(term),
		    scored = [];

		if (needle === '' || index === null) {
			return [];
		}

		$.each(index, function eachRow(i, row) {
			var label = _norm(row.label),
			    path  = _norm(row.breadcrumb),
			    score = -1;

			if (label.indexOf(needle) === 0) {
				score = 0;
			} else if (label.indexOf(needle) > -1) {
				score = 1;
			} else if (label.replace(/ /g, '').indexOf(needle.replace(/ /g, '')) > -1) {
				// SET-71 QA: WHERE THE SPACE FALLS IS ALSO THE ADVISOR'S GUESS. Stripping
				// punctuation turns "CP|central" into one word, so "cpcentral" finds it and
				// "cp central" - which is just as likely for a name written without a space -
				// did not. Comparing again with every space gone covers both spellings, and it
				// ranks below a real label match so it never displaces one.
				score = 2;
			} else if (_aliasHit(row, needle)) {
				// SET-72: a tab with a single block carries that block's words as aliases rather
				// than emitting a second row for the same destination. They are matched on and
				// never shown - "hotel sheet settings" finds the Hotel Sheets tab.
				score = 3;
			} else if (path.indexOf(needle) > -1) {
				score = 4;
			}

			if (score > -1) {
				scored.push({
					rank : (KIND_RANK[row.kind] === undefined ? 9 : KIND_RANK[row.kind]),
					score: score,
					order: i,
					row  : row
				});
			}
		});

		// KIND OUTRANKS MATCH QUALITY, deliberately - broadest destination first, then the
		// settings inside it. Ordering by match quality alone put "Password Guidelines" above
		// "Change Password" for the term "password", because the guidelines block happens to
		// START with the word while the tab has it seventh. The advisor almost always wants the
		// place before the control inside it, and a prefix match is an accident of wording.
		//
		// Within a kind the original ordering still applies: better match first, then tree order.
		scored.sort(function byRank(a, b) {
			if (a.rank !== b.rank) {
				return a.rank - b.rank;
			}

			return (a.score === b.score ? a.order - b.order : a.score - b.score);
		});

		return $.map(scored.slice(0, MAX_RESULTS), function pick(hit) {
			return hit.row;
		});
	};

	_render = function render(matches)
	{
		var $out = _results(),
		    html = '';

		rows   = matches;
		active = -1;

		if (!matches.length) {
			_close();

			return;
		}

		html += '<ul class="my-settings-search-list" role="listbox" aria-label="Search results">';

		$.each(matches, function eachMatch(i, row) {
			html += '<li class="my-settings-search-result" role="option" aria-selected="false"'
				+ ' id="my-settings-search-result-' + i + '" data-search-index="' + i + '">'
				// SET-72 QA: BOTH OF THESE ELLIPSIS, so both say their full text on hover.
				// "Communication Tools > Trip & Broch..." hid the tab, which is the half that
				// tells two same-named rows apart - searching "sheets" returned Include
				// Excursions twice with no way to see which sheet each belonged to.
				//
				// title, NOT a tooltip component: these rows are inside a listbox that closes on
				// outside-click, and a second widget on the same element is what broke that
				// elsewhere. The native one needs no teardown when the list is emptied.
				+ '<span class="my-settings-search-result-name" title="' + _esc(row.label) + '">'
				+ _esc(row.label) + '</span>'
				+ '<span class="my-settings-search-result-kind">'
				+ _esc(CATEGORY[row.kind] || 'Setting') + '</span>'
				+ '<span class="my-settings-search-result-path" title="' + _esc(row.breadcrumb) + '">'
				+ _esc(row.breadcrumb) + '</span>'
				+ '</li>';
		});

		html += '</ul>';

		$out.html(html).prop('hidden', false);

		// The combobox roles SET-9 deliberately left off the input until there was a listbox for
		// them to describe. Announcing a combobox that never expands would have been worse than
		// announcing a search field.
		_input()
			.attr('role', 'combobox')
			.attr('aria-expanded', 'true')
			.attr('aria-controls', 'my-settings-search-results')
			.removeAttr('aria-activedescendant');
	};

	/**
	 * SET-164: run the search for a term and show or hide the list accordingly.
	 *
	 * LIFTED OUT OF THE `input` HANDLER so focusing the field can reopen the list without
	 * retyping. Those were the same three lines, and the reopen is the whole of AC 4.
	 */
	_search = function search(term)
	{
		if ($.trim(term) === '') {
			_close();
			return;
		}

		_load(function ready() {
			// The field may have moved on while the index was in flight.
			_render(_match(_input().val()));
		});
	};

	_close = function close()
	{
		_results().empty().prop('hidden', true);

		rows   = [];
		active = -1;

		_input()
			.attr('aria-expanded', 'false')
			.removeAttr('aria-activedescendant');
	};

	_move = function move(step)
	{
		var row_el;

		if (!rows.length) {
			return;
		}

		active = (active + step + rows.length) % rows.length;

		_results().find('.my-settings-search-result')
			.removeClass('is-active')
			.attr('aria-selected', 'false')
			.eq(active)
			.addClass('is-active')
			.attr('aria-selected', 'true');

		_input().attr('aria-activedescendant', 'my-settings-search-result-' + active);

		// SET-72 QA, AC 3: THE LIST SCROLLS TO THE ROW, which it never did. The list is capped
		// at ten and its own box scrolls, so arrowing past the fold moved the highlight onto a
		// row the advisor could not see and Enter then opened something they had not read.
		//
		// 'nearest' rather than 'center': it moves only when the row is actually out of view, so
		// walking the visible rows does not jolt the list under the cursor.
		row_el = _results().find('.my-settings-search-result').get(active);

		if (row_el && typeof row_el.scrollIntoView === 'function') {
			row_el.scrollIntoView({ block: 'nearest' });
		}
	};

	_choose = function choose(i)
	{
		var row = rows[i];

		if (!row || !row.action) {
			return;
		}

		// Held rather than acted on: the panel it belongs to has not arrived, and if the guard
		// raises the discard dialog it may never. The action is kept alongside the anchor so a
		// render for some other panel cannot consume it.
		pending = {
			anchor: row.anchor,
			action: row.action,
			kind  : row.kind
		};

		// HELD BEFORE IT IS CLEARED. The guard may raise the discard dialog, and Keep editing
		// then leaves the advisor on the panel they were editing with the search box emptied for
		// a jump that never happened - so they retype the search to get back to where they were.
		last_term = _input().val();

		_close();
		_input().val('');

		// requestNavigate(), not load(): this is the seam that asks SET-76's guard first.
		Adzuki.MySettings.requestNavigate('/MySettings/' + row.action);
	};

	/**
	 * Scrolls the match into view and marks it, once the panel carrying it has rendered.
	 *
	 * A 'section' or 'tab' hit has no anchor - arriving on the right panel IS the result - so
	 * there is nothing to highlight and nothing to scroll.
	 */
	_consume = function consume()
	{
		var target, $target;

		if (pending === null) {
			return;
		}

		// a panel has arrived for this jump, so the term is not coming back - whichever branch
		// below runs, it was not cancelled
		last_term = '';

		if (!pending.anchor) {
			pending = null;

			return;
		}

		target = document.getElementById('setting-' + pending.anchor);

		// Not on this panel. Either the advisor chose Keep editing and later navigated somewhere
		// else entirely, or the anchor addresses nothing - drop it rather than glow at whatever
		// happens to be on screen.
		if (target === null) {
			pending = null;

			return;
		}

		$target = $(target);
		pending = null;

		if (typeof target.scrollIntoView === 'function') {
			target.scrollIntoView({ block: 'center' });
		}

		// The label is the first of a two-column grid pair, so the control beside it is a sibling
		// rather than a child - marking both is what makes the whole row read as the hit.
		$target.addClass(HIT_CLASS);
		$target.next('.my-settings-field-control').addClass(HIT_CLASS);

		window.setTimeout(function fade() {
			$target.removeClass(HIT_CLASS);
			$target.next('.my-settings-field-control').removeClass(HIT_CLASS);
		}, HIGHLIGHT_MS);
	};

	$(document).ready(function documentReady() {
		if (!Adzuki.MySettings) {
			return;
		}

		$(document).on('input', '#my-settings-search-input', function typed() {
			_search($(this).val());
		});

		// SET-164: REOPEN ON FOCUS, so a list closed by clicking away comes back rather than
		// needing the term retyped. _close() never touches the field, so the text is still there.
		$(document).on('focusin', '#my-settings-search-input', function refocused() {
			_search($(this).val());
		});

		$(document).on('keydown', '#my-settings-search-input', function keys(e) {
			if (e.which === 40) { e.preventDefault(); _move(1); return; }
			if (e.which === 38) { e.preventDefault(); _move(-1); return; }

			// SET-164: TAB ALWAYS LEAVES THE WHOLE WIDGET, so it can close unconditionally. The
			// results are <li role="option"> with no tabindex, so there is nothing inside the list
			// for focus to move to - which is why this needs no relatedTarget check and why a
			// focusout handler would have been the wrong tool: focusout also fires when the
			// advisor MOUSES onto a result, and closing there would kill the click.
			//
			// NOT preventDefault - the advisor asked to move focus on, and they should.
			if (e.which === 9 && rows.length) {
				_close();

				return;
			}

			if (e.which === 27 && rows.length) {
				// Closes the dropdown without closing the modal - my_settings.js reads ESC on the
				// document, so this has to stop the bubble or one key would do both.
				e.stopPropagation();
				_close();

				return;
			}

			if (e.which === 13 && active > -1) {
				e.preventDefault();
				_choose(active);
			}
		});

		// SET-164: A POINTER OUTSIDE THE WIDGET CLOSES THE LIST.
		//
		// MOUSEDOWN RATHER THAN CLICK, and the two are not interchangeable here. Both this and
		// the result handler below are delegated on `document`, so with `click` the two would
		// race on registration order and whichever ran first would decide whether picking a
		// result worked. mousedown is unambiguously earlier, and the result container is
		// excluded below - so a press on a result does not close the list and its click lands.
		//
		// GUARDED ON rows.length so this costs one property read per click while the list is
		// shut, which is almost always.
		$(document).on('mousedown', function outside(e) {
			if (!rows.length) {
				return;
			}

			if ($(e.target).closest('#my-settings-search-input, #my-settings-search-results').length) {
				return;
			}

			_close();
		});

		$(document).on('click', '.my-settings-search-result', function picked() {
			_choose(parseInt($(this).attr('data-search-index'), 10));
		});

		$(document).on('my-settings:rendered', _consume);

		// SET-72 QA: A JUMP THAT WAS CALLED OFF LEAVES NOTHING BEHIND.
		//
		// The held target used to be dropped only when a render arrived without its anchor on
		// it - which never happens if the advisor chooses Keep editing and stays put. It then
		// sat there until some later navigation, and glowed at whatever that turned out to be:
		// choose Default Menu Type with unsaved edits, Keep editing, undo the edit by hand, open
		// CP|central from the nav, and Default Menu Type lit up as though it had been searched
		// for. Cancelling is now its own signal rather than something inferred later.
		//
		// The term comes back with it: it was cleared on the way into a jump that did not
		// happen, so without this the advisor retypes the search to get back where they were.
		$(document).on('my-settings:navigation-cancelled', function cancelled() {
			pending = null;

			if (last_term === '') {
				return;
			}

			_input().val(last_term);
			last_term = '';
		});

		// The index belongs to the viewer and the session, so it goes when the modal does.
		$(document).on('my-settings:closed', function closed() {
			index     = null;
			pending   = null;
			last_term = '';
			_close();
		});
	});
}(jQuery, window.Adzuki || {}));
